import {generateTrackingId} from '@udemy/event-tracking';

import {
    practiceCourses,
    prepCourses,
} from 'src/features/all-certification-vouchers-landing-page/components/course-units/utils/data';
import {
    getTotalHours,
    loadInstructors,
    loadOutcomes,
} from 'src/features/all-certification-vouchers-landing-page/components/course-units/utils/helpers';
import {useCoursesQuery, CertificationVoucherProviderType} from 'src/generated/graphql';
import {mapInstructorLevelFromGraphQLEnum} from 'src/lib/gql-mappers';
import {CourseInput} from 'src/types/certification-vouchers';

export type CourseProvider = CertificationVoucherProviderType | 'all';

export const useCourses = (provider: CourseProvider = 'all') => {
    const filteredPracticeCourses =
        provider === 'all'
            ? practiceCourses
            : practiceCourses.filter((course) => course.provider === provider);

    const filteredPrepCourses =
        provider === 'all'
            ? prepCourses
            : prepCourses.filter((course) => course.provider === provider);

    const courseIds = [
        ...filteredPracticeCourses.map((course) => course.course_id.toString()),
        ...filteredPrepCourses.map((course) => course.course_id.toString()),
    ];

    const hasCourseIds = courseIds.length > 0;
    const {data, isLoading} = useCoursesQuery(
        {ids: courseIds},
        {
            enabled: hasCourseIds,
            cacheTime: 0,
            staleTime: 0,
            keepPreviousData: false,
        },
    );

    const effectiveData = hasCourseIds ? data : undefined;

    const isLoadingCourses = hasCourseIds && isLoading;

    const practiceCourseTrackingIds = filteredPracticeCourses.map(() => generateTrackingId());
    const prepCourseTrackingIds = filteredPrepCourses.map(() => generateTrackingId());

    function populateCourses(coursesData: CourseInput[], trackingIds: string[]) {
        if (!effectiveData || !coursesData.length) return [];
        return coursesData
            .map((course, index) => {
                const courseData = effectiveData?.courses.find(
                    (c) => c?.id === course.course_id.toString(),
                );

                // Skip courses that don't have valid data or ID
                if (!courseData?.id) return null;

                const hasClosedCaption = courseData?.captions?.some(
                    (caption) => caption.type === 'CLOSED_CAPTION',
                );
                const has508ClosedCaption = courseData?.captions?.some(
                    (caption) => caption.type === 'SUBTITLE',
                );

                return {
                    id: Number(courseData.id),
                    title: courseData?.title,
                    headline: courseData?.headline,
                    course_url: courseData?.urlCourseTaking,
                    url: courseData?.urlCourseLanding,
                    image_240x135: courseData?.images?.px480x270,
                    visible_instructors: loadInstructors(courseData?.instructors),
                    rating: courseData?.rating?.average,
                    num_reviews: courseData?.rating?.count,
                    last_update_date: courseData?.publishedOn,
                    content_info: getTotalHours(courseData?.durationInSeconds),
                    duration_in_seconds: courseData?.durationInSeconds,
                    instructional_level: mapInstructorLevelFromGraphQLEnum(courseData?.level),
                    has_closed_caption: hasClosedCaption,
                    has_508_closed_captions: has508ClosedCaption,
                    objectives_summary: loadOutcomes(courseData?.learningOutcomes),
                    is_practice_test_course: course.is_practice_test_course,
                    tracking_id: trackingIds[index],
                    buyable_object_type: 'course',
                    type: 'course',
                };
            })
            .filter((course) => course !== null);
    }

    return {
        prepCourses: populateCourses(filteredPrepCourses, prepCourseTrackingIds),
        practiceCourses: populateCourses(filteredPracticeCourses, practiceCourseTrackingIds),
        isLoading: isLoadingCourses,
    };
};
